import { fuzzyRank } from "./fuzzy";
import { type Token, tokenize } from "@package/scratchpad-utils";
import { innermostCallAt, scanCalls } from "./scan";

export type CompletionTarget = { start: number; end: number; prefix: string };

export function completionTarget(text: string, caret: number): CompletionTarget | null {
  if (!text.startsWith("=") || caret < 1) return null;
  if (caret < text.length && /[A-Za-z0-9_]/.test(text[caret]!)) return null;
  let start = caret;
  while (start > 1 && /[A-Za-z_]/.test(text[start - 1]!)) start--;
  if (start === caret) return null;
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1, start));
  } catch {
    return null;
  }
  const prev = tokens[tokens.length - 1];
  if (prev && prev.kind !== "op" && prev.kind !== "lparen" && prev.kind !== "comma") return null;
  return { start, end: caret, prefix: text.slice(start, caret) };
}

export type SignatureContext = { name: string; argIndex: number };

export function signatureContext(text: string, caret: number): SignatureContext | null {
  if (!text.startsWith("=") || caret < 1) return null;
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1));
  } catch {
    return null;
  }
  const stack: Array<{ name: string | null; argIndex: number }> = [];
  let pending: string | null = null;
  for (const t of tokens) {
    if (t.span.end + 1 > caret) break;
    switch (t.kind) {
      case "func":
        pending = t.name;
        break;
      case "lparen":
        stack.push({ name: pending, argIndex: 0 });
        pending = null;
        break;
      case "rparen":
        stack.pop();
        break;
      case "comma": {
        const top = stack[stack.length - 1];
        if (top) top.argIndex++;
        break;
      }
      default:
        pending = null;
        break;
    }
  }
  for (let i = stack.length - 1; i >= 0; i--) {
    const frame = stack[i]!;
    if (frame.name) return { name: frame.name.toUpperCase(), argIndex: frame.argIndex };
  }
  return null;
}

export type CurveArgTarget =
  | { arg: "product"; start: number; end: number; prefix: string }
  | { arg: "field"; start: number; end: number; prefix: string; productId: string | null };

export function curveArgCompletion(text: string, caret: number): CurveArgTarget | null {
  if (!text.startsWith("=") || caret < 1) return null;
  const call = innermostCallAt(scanCalls(text), caret);
  if (!call || call.name?.toUpperCase() !== "CURVE") return null;
  let argIndex = 0;
  for (let i = call.argStarts.length - 1; i >= 0; i--) {
    if (call.argStarts[i]! <= caret) {
      argIndex = i;
      break;
    }
  }
  if (argIndex > 1) return null;
  const arg0 = call.strings.find((s) => s.argIndex === 0)?.value ?? null;
  const containing = call.strings.find((s) => s.start <= caret && caret <= s.end);
  let start: number;
  let end: number;
  let prefix: string;
  if (containing) {
    start = containing.start;
    end = containing.end;
    const contentStart = start + 1;
    prefix = text.slice(contentStart, Math.min(Math.max(caret, contentStart), contentStart + containing.value.length));
  } else {
    if (call.strings.some((s) => s.argIndex === argIndex && s.end <= caret)) return null;
    start = call.argStarts[argIndex]!;
    while (start < caret && text[start] === " ") start++;
    end = caret;
    prefix = text.slice(start, caret);
  }
  if (argIndex === 0) return { arg: "product", start, end, prefix };
  return { arg: "field", start, end, prefix, productId: arg0 };
}

export function curveCallArgs(text: string, caret: number): { product: string | null; field: string | null } | null {
  if (!text.startsWith("=") || caret < 1) return null;
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1));
  } catch {
    return null;
  }
  type Frame = { name: string | null; start: number; argIndex: number; arg0: string | null; arg1: string | null };
  const stack: Frame[] = [];
  const closed: Array<Frame & { end: number }> = [];
  let pending: { name: string; start: number } | null = null;
  for (const t of tokens) {
    switch (t.kind) {
      case "func":
        pending = { name: t.name, start: t.span.start + 1 };
        break;
      case "lparen":
        stack.push({
          name: pending?.name ?? null,
          start: pending?.start ?? t.span.start + 1,
          argIndex: 0,
          arg0: null,
          arg1: null,
        });
        pending = null;
        break;
      case "rparen": {
        const frame = stack.pop();
        if (frame) closed.push({ ...frame, end: t.span.end + 1 });
        break;
      }
      case "comma": {
        const top = stack[stack.length - 1];
        if (top) top.argIndex++;
        break;
      }
      case "str": {
        const top = stack[stack.length - 1];
        if (top) {
          if (top.argIndex === 0 && top.arg0 === null) top.arg0 = t.value;
          else if (top.argIndex === 1 && top.arg1 === null) top.arg1 = t.value;
        }
        pending = null;
        break;
      }
      default:
        pending = null;
        break;
    }
  }
  for (const frame of stack) closed.push({ ...frame, end: text.length });
  let best: (Frame & { end: number }) | null = null;
  for (const frame of closed) {
    if (frame.name?.toUpperCase() !== "CURVE") continue;
    if (caret < frame.start || caret > frame.end) continue;
    if (!best || frame.start > best.start) best = frame;
  }
  return best ? { product: best.arg0, field: best.arg1 } : null;
}

export function enclosingCall(text: string, caret: number, name: string): { start: number; end: number } | null {
  if (!text.startsWith("=") || caret < 1) return null;
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1));
  } catch {
    return null;
  }
  const stack: Array<{ name: string | null; start: number }> = [];
  let pending: { name: string; start: number } | null = null;
  for (const t of tokens) {
    switch (t.kind) {
      case "func":
        pending = { name: t.name, start: t.span.start + 1 };
        break;
      case "lparen":
        stack.push(pending ?? { name: null, start: t.span.start + 1 });
        pending = null;
        break;
      case "rparen": {
        const frame = stack.pop();
        const end = t.span.end + 1;
        if (frame?.name?.toUpperCase() === name && frame.start <= caret && caret <= end) {
          return { start: frame.start, end };
        }
        break;
      }
      default:
        pending = null;
        break;
    }
  }
  for (let i = stack.length - 1; i >= 0; i--) {
    const frame = stack[i]!;
    if (frame.name?.toUpperCase() === name && frame.start <= caret) return { start: frame.start, end: text.length };
  }
  return null;
}

export function spliceArg(
  text: string,
  target: { start: number; end: number },
  inner: string,
  separator: "," | ")",
): { text: string; caret: number } {
  const inserted = `"${inner}"`;
  const after = text.slice(target.end);
  let skip = 0;
  while (after[skip] === " ") skip++;
  const spliced = text.slice(0, target.start) + inserted;
  if (after[skip] === separator) {
    return { text: spliced + after, caret: target.start + inserted.length + skip + 1 };
  }
  return { text: spliced + separator + after, caret: target.start + inserted.length + 1 };
}

export function matchFunctions(prefix: string, names: readonly string[]): string[] {
  return fuzzyRank(prefix, names, (name) => name);
}

export function applyCompletion(
  text: string,
  target: { start: number; end: number },
  name: string,
): { text: string; caret: number } {
  const insert = text[target.end] === "(" ? name : `${name}(`;
  return {
    text: text.slice(0, target.start) + insert + text.slice(target.end),
    caret: target.start + name.length + 1,
  };
}
